'use client';

import { usePathname } from 'next/navigation';
import Link from 'next/link';
import { cn } from '@/lib/utils';
import { motion } from 'framer-motion';
import {
  LayoutDashboard, FileText, Radio, Users,
  BarChart2, BarChart3, Lightbulb, Settings, Sparkles, Brain, Calendar
} from 'lucide-react';

interface NavItem {
  name: string;
  href: string;
  /** Optional badge count rendered to the right of the label (e.g. open deals on Sales) */
  count?: number;
}

interface DashboardNavProps {
  items: NavItem[];
}

// Map icons to nav items
const getIconForNavItem = (name: string) => {
  const icons: Record<string, any> = {
    'Overview': LayoutDashboard,
    'Posts': FileText,
    'Moments': Sparkles,
    'Momento': Brain,
    'Podcast': Radio,
    'Audience': Users,
    'Subscribers': Users,
    'Stats': BarChart2,
    'Sales': BarChart3,
    'Recommendations': Lightbulb,
    'Bookings': Calendar,
    'Settings': Settings
  };
  return icons[name] || LayoutDashboard;
};

/**
 * Dashboard navigation component
 * Client component that shows the current navigation state
 */
export default function DashboardNav({ items }: DashboardNavProps) {
  const pathname = usePathname();

  // Hide navigation on edit pages
  if (pathname?.includes('/edit')) {
    return null;
  }

  return (
    <div className="sticky top-14 z-30 w-full border-b border-gray-200 dark:border-gray-800 bg-background">
      <div className="container mx-auto px-4">
        <nav className="relative" aria-label="Dashboard navigation">
          {/* Scrollable Navigation with active indicator */}
          <div className="overflow-auto scrollbar-hide -mb-px">
            <ul className="flex min-w-full whitespace-nowrap py-1 list-none">
              {items.map((item, index) => {
                const isActive = 
                  pathname === item.href || 
                  (item.href !== '/dashboard' && pathname?.startsWith(item.href));
                
                const Icon = getIconForNavItem(item.name);
                  
                return (
                  <li key={item.href} className={cn("m-0 p-0", index > 0 ? "ml-2" : "")}>
                    <Link
                      href={item.href}
                      className={cn(
                        "inline-flex items-center px-4 py-3 text-sm font-medium rounded-md transition-all relative",
                        "hover:bg-muted/60",
                        isActive
                          ? "text-[#7047EB]"
                          : "text-muted-foreground hover:text-foreground"
                      )}
                      aria-current={isActive ? 'page' : undefined}
                    >
                      <Icon className="mr-2 h-4 w-4" />
                      {item.name}
                      {typeof item.count === 'number' && item.count > 0 && (
                        <span
                          className={cn(
                            "ml-2 font-mono text-[10px] tracking-wider",
                            isActive ? "text-[#7047EB] font-semibold" : "text-app-text-3"
                          )}
                          aria-label={`${item.count} active`}
                        >
                          {item.count}
                        </span>
                      )}

                      {isActive && (
                        <motion.div 
                          layoutId="activeNavIndicator"
                          className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#7047EB] rounded-full mx-2"
                          initial={false}
                          transition={{ type: "spring", stiffness: 500, damping: 30 }}
                        />
                      )}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>
          
          {/* Gradient indicators for horizontal scroll */}
          <div className="absolute right-0 top-0 bottom-0 w-8 bg-gradient-to-l from-background to-transparent pointer-events-none" />
          <div className="absolute left-0 top-0 bottom-0 w-8 bg-gradient-to-r from-background to-transparent pointer-events-none" />
        </nav>
      </div>
    </div>
  );
}